<script lang="ts">
  import { Link } from 'svelte-routing'
  import { apiBaseUrl } from '$lib/api/base-url'
  import FormField from '../../components/FormField.svelte'

  const token = new URLSearchParams(window.location.search).get('token')
  const INVALID_LINK_MESSAGE = 'This link is invalid or has expired; ask an admin to resend it.'

  let password = $state('')
  let confirm = $state('')
  let submitting = $state(false)
  let done = $state(false)
  let error = $state(token ? '' : INVALID_LINK_MESSAGE)

  const passwordError = $derived(
    password.length > 0 && password.length < 8 ? 'Must be at least 8 characters' : '',
  )
  const confirmError = $derived(
    confirm.length > 0 && confirm !== password ? 'Passwords do not match' : '',
  )
  const canSubmit = $derived(!!token && password.length >= 8 && password === confirm && !submitting)

  async function handleSubmit(e: SubmitEvent): Promise<void> {
    e.preventDefault()
    if (!token || !canSubmit) return
    submitting = true
    error = ''
    try {
      const res = await fetch(`${apiBaseUrl()}/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      })
      if (res.ok) {
        done = true
      } else {
        error = INVALID_LINK_MESSAGE
      }
    } catch {
      error = 'Could not set your password. Please check your connection and try again.'
    } finally {
      submitting = false
    }
  }
</script>

<div class="login-shell">
  <div class="login-card">
    <h1>RentReadyPet</h1>
    <p class="subtitle">Set your password</p>

    {#if done}
      <p class="success">Your password has been set.</p>
      <Link to="/login">Sign in</Link>
    {:else if !token}
      <p class="error-message">{error}</p>
    {:else}
      <form onsubmit={handleSubmit}>
        <FormField
          label="Password"
          name="password"
          type="password"
          bind:value={password}
          required
          error={passwordError}
        />
        <FormField
          label="Confirm password"
          name="confirm"
          type="password"
          bind:value={confirm}
          required
          error={confirmError}
        />
        {#if error}
          <p class="error-message">{error}</p>
        {/if}
        <button type="submit" disabled={!canSubmit}>
          {submitting ? 'Setting password…' : 'Set password'}
        </button>
      </form>
    {/if}
  </div>
</div>

<style>
  .login-shell {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: var(--surface-3);
  }
  .login-card {
    background: var(--surface-2);
    padding: 32px;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 380px;
  }
  h1 {
    margin: 0 0 4px;
    font-size: 1.5rem;
  }
  .subtitle {
    margin: 0 0 20px;
    color: var(--muted);
  }
  .success {
    color: var(--ok-ink);
    margin-bottom: 12px;
  }
  .error-message {
    color: var(--err-ink);
    font-size: 0.875rem;
    margin: 0 0 12px;
  }
  button {
    width: 100%;
    padding: 10px;
    min-height: 44px;
    background: var(--brand);
    color: white;
    border: none;
    border-radius: 4px;
    font-weight: 500;
    cursor: pointer;
  }
  button:hover:not(:disabled) {
    background: var(--brand-hover);
  }
  button:disabled {
    background: color-mix(in srgb, var(--brand) 40%, var(--surface-2));
    cursor: not-allowed;
  }
</style>
